import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, Send, Trash2, Loader2, Sparkles, Calendar,
  MessageCircle, Lightbulb, Ear, Flower2, Mic, MicOff,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_META = {
  mama:     { label: 'Som en mamma',    emoji: '💛', bg: '#FFE8E8', accent: '#FF8FA3' },
  friend:   { label: 'Som en bästis',    emoji: '🌸', bg: '#FFE8F5', accent: '#FFC8DD' },
  wise:     { label: 'Som en klok vän',  emoji: '🌿', bg: '#E8F5E8', accent: '#B9FBC0' },
  listener: { label: 'Bara lyssna',      emoji: '🌙', bg: '#E8F0FF', accent: '#A2D2FF' },
  pep:      { label: 'Peppa mig snällt', emoji: '✨', bg: '#FFF7E0', accent: '#FFE8A3' },
  nudge:    { label: 'Knuff i rätt riktning', emoji: '🧭', bg: '#EFE8FF', accent: '#C8B6FF' },
};

const MODE_ORDER = ['mama', 'friend', 'wise', 'listener', 'pep', 'nudge'];

const CATEGORY_LABEL = {
  task: { label: 'Uppgift', emoji: '✅' },
  postit: { label: 'Post-it', emoji: '📌' },
  rest: { label: 'Vila', emoji: '🛋️' },
  selfcare: { label: 'Omsorg', emoji: '🫶' },
  call: { label: 'Kontakta', emoji: '📞' },
};

function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DictateMic({ onText, disabled }) {
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const supported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  if (!supported) return null;

  const toggle = () => {
    if (recording && recRef.current) {
      recRef.current.stop();
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'sv-SE';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const txt = Array.from(e.results).map((r) => r[0].transcript).join(' ');
      if (txt) onText(txt);
    };
    rec.onend = () => setRecording(false);
    rec.onerror = () => setRecording(false);
    rec.start();
    recRef.current = rec;
    setRecording(true);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      className={`neo-btn p-2 ${recording ? 'bg-red-200 animate-pulse' : 'bg-white'}`}
      title={recording ? 'Stoppa inspelning' : 'Diktera'}
      data-testid="diary-dictate-btn"
      aria-label="Diktera"
    >
      {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
}

function NudgeStepCard({ step, onAddTask, onAddPostit, busy }) {
  const [status, setStatus] = useState('idle'); // idle | added-task | added-postit | dismissed
  const cat = CATEGORY_LABEL[step.category] || CATEGORY_LABEL.task;
  const defaultAction = step.category === 'postit' ? 'postit'
    : step.category === 'task' ? 'task'
    : null;

  const onAdd = async (kind) => {
    try {
      if (kind === 'task') {
        await onAddTask(step);
        setStatus('added-task');
      } else {
        await onAddPostit(step);
        setStatus('added-postit');
      }
    } catch {
      /* toast handled by caller */
    }
  };

  const done = status === 'added-task' || status === 'added-postit';
  return (
    <div
      className={`neo-card p-3 bg-white ${done ? 'opacity-60' : ''}`}
      data-testid="diary-nudge-step"
    >
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-xl border-[3px] border-black bg-[#FDF0D5] flex items-center justify-center text-lg shrink-0 shadow-[2px_2px_0_#000]">
          <span aria-hidden>{step.emoji}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`font-bold text-sm ${done ? 'line-through' : ''}`}>{step.title}</p>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black bg-white">
              {cat.emoji} {cat.label}
            </span>
          </div>
          {step.why && <p className="text-xs text-black/70 mt-0.5 leading-snug">{step.why}</p>}
        </div>
      </div>
      {!done && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {defaultAction !== 'postit' && (
            <button
              onClick={() => onAdd('task')}
              disabled={busy}
              className="neo-btn px-2 py-1 text-[11px] font-bold bg-[#B9FBC0] flex items-center gap-1"
              data-testid="diary-nudge-add-task"
            >
              ✅ Lägg till som uppgift
            </button>
          )}
          {defaultAction !== 'task' && (
            <button
              onClick={() => onAdd('postit')}
              disabled={busy}
              className="neo-btn px-2 py-1 text-[11px] font-bold bg-[#FFF7A0] flex items-center gap-1"
              data-testid="diary-nudge-add-postit"
            >
              📌 Spara som post-it
            </button>
          )}
          <button
            onClick={() => setStatus('dismissed')}
            className="neo-btn px-2 py-1 text-[11px] bg-white"
          >
            Inte just nu
          </button>
        </div>
      )}
      {status === 'added-task' && <p className="text-[11px] mt-2 text-black/60">✨ Lagt till i Tasks!</p>}
      {status === 'added-postit' && <p className="text-[11px] mt-2 text-black/60">📌 Sparad som post-it!</p>}
      {status === 'dismissed' && <p className="text-[11px] mt-2 text-black/60 italic">Det är okej. Kanske senare.</p>}
    </div>
  );
}

function EntryCard({ entry, onDelete, onAddTask, onAddPostit }) {
  const meta = MODE_META[entry.mode] || MODE_META.mama;
  return (
    <div className="neo-card bg-white p-0 overflow-hidden" data-testid="diary-entry-card">
      {/* Timestamp + mode */}
      <div
        className="px-4 py-2 flex items-center justify-between gap-2 border-b-[3px] border-black"
        style={{ background: meta.bg }}
      >
        <p className="text-xs font-bold flex items-center gap-1.5 opacity-80">
          <Calendar className="w-3 h-3" /> {fmtDate(entry.created_at)}
        </p>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-[2px] border-black bg-white">
            {meta.emoji} {meta.label}
          </span>
          <button
            onClick={() => onDelete(entry.id)}
            className="text-black/50 hover:text-red-600 p-1"
            aria-label="Ta bort inlägget"
            data-testid="diary-entry-delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* User text */}
      <div className="px-4 py-3 bg-[#FFFEF8]">
        <p className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="diary-entry-text">
          {entry.text}
        </p>
      </div>
      {/* Reply */}
      <div
        className="px-4 py-3 border-t-[3px] border-black"
        style={{ background: meta.bg }}
      >
        {entry.structured ? (
          <div className="space-y-3">
            {entry.structured.validation && (
              <div className="flex items-start gap-2">
                <span className="text-xl leading-none mt-0.5">{meta.emoji}</span>
                <p className="whitespace-pre-wrap text-sm leading-relaxed flex-1 italic">
                  {entry.structured.validation}
                </p>
              </div>
            )}
            {entry.structured.steps?.length > 0 && (
              <div className="space-y-2" data-testid="diary-nudge-steps">
                {entry.structured.steps.map((s, i) => (
                  <NudgeStepCard
                    key={i}
                    step={s}
                    onAddTask={onAddTask}
                    onAddPostit={onAddPostit}
                  />
                ))}
              </div>
            )}
            {entry.structured.closing && (
              <p className="text-sm leading-relaxed text-center font-bold pt-1">
                {entry.structured.closing}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <span className="text-xl leading-none mt-0.5">{meta.emoji}</span>
            <p className="whitespace-pre-wrap text-sm leading-relaxed flex-1" data-testid="diary-entry-reply">
              {entry.reply}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Diary() {
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState('');
  const [mode, setMode] = useState('mama');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const textRef = useRef(null);

  const fetchEntries = async () => {
    try {
      const { data } = await axios.get(`${API}/diary/entries`, { withCredentials: true });
      setEntries(data.entries || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []);

  const send = async () => {
    const t = text.trim();
    if (!t) {
      toast.error('Skriv något först 💛');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/diary/entries`,
        { text: t, mode },
        { withCredentials: true, timeout: 90000 }
      );
      setEntries((prev) => [data, ...prev]);
      setText('');
      toast.success('Svaret är här 💛');
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kunde inte svara just nu — prova igen om en stund');
    } finally {
      setSending(false);
      textRef.current?.focus();
    }
  };

  const del = async (id) => {
    if (!window.confirm('Ta bort detta inlägg? Det går inte att ångra.')) return;
    try {
      await axios.delete(`${API}/diary/entries/${id}`, { withCredentials: true });
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const addTaskFromStep = async (step) => {
    try {
      await axios.post(
        `${API}/tasks`,
        {
          title: step.title,
          description: step.why || '',
          priority: 'medium',
          lane: 'today',
          points: 5,
        },
        { withCredentials: true }
      );
      toast.success(`✅ "${step.title}" tillagd i Tasks`);
    } catch (e) {
      toast.error('Kunde inte lägga till task');
      throw e;
    }
  };

  const addPostitFromStep = async (step) => {
    try {
      const content = step.emoji ? `${step.emoji} ${step.title}` : step.title;
      await axios.post(
        `${API}/stickies`,
        { content: content.slice(0, 200), color: 'yellow' },
        { withCredentials: true }
      );
      toast.success('📌 Post-it sparad!');
    } catch (e) {
      toast.error('Kunde inte spara post-it');
      throw e;
    }
  };

  const meta = MODE_META[mode];

  return (
    <div className="space-y-5" data-testid="diary-page">
      {/* Warm header */}
      <header
        className="neo-card p-5 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #FFE8E8 0%, #FFE8F5 50%, #E8F0FF 100%)' }}
      >
        <div className="relative z-10">
          <h2 className="font-['Fredoka'] font-bold text-2xl flex items-center gap-2">
            <Heart className="w-6 h-6 fill-current text-[#FF8FA3]" /> Kära dagbok
          </h2>
          <p className="text-sm text-black/75 mt-1.5 leading-relaxed max-w-xl">
            Skriv precis vad du vill, precis som det är. Inget är för litet eller för stort.
            Du blir alltid hörd här, och får ett varmt svar tillbaka. 🌸
          </p>
          <p className="text-[11px] text-black/50 mt-1 italic">
            🔒 Helt privat. Bara du ser det.
          </p>
        </div>
        <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 leading-none">💛</div>
      </header>

      {/* Compose box */}
      <div className="neo-card bg-white p-4 space-y-3" data-testid="diary-compose">
        {/* Mode chips */}
        <div>
          <p className="text-xs font-bold mb-2 opacity-70">Vad behöver du idag?</p>
          <div className="flex gap-2 flex-wrap">
            {MODE_ORDER.map((k) => {
              const m = MODE_META[k];
              const active = mode === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setMode(k)}
                  className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 transition ${active ? 'translate-y-[-1px]' : 'opacity-80'}`}
                  style={{ background: active ? m.accent : 'white' }}
                  data-testid={`diary-mode-${k}`}
                >
                  <span>{m.emoji}</span>
                  <span>{m.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Text area */}
        <div className="relative">
          <textarea
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Skriv precis det som snurrar i huvudet just nu..."
            rows={6}
            maxLength={8000}
            disabled={sending}
            className="neo-input w-full resize-y min-h-[140px] leading-relaxed"
            style={{ background: meta.bg }}
            data-testid="diary-textarea"
          />
          <div className="absolute bottom-2 right-2 flex items-center gap-2">
            <DictateMic
              onText={(t) => setText((p) => (p ? `${p} ${t}` : t))}
              disabled={sending}
            />
          </div>
          <div className="flex items-center justify-between mt-1 text-[10px] text-black/50">
            <span>Du kan diktera med mikrofonen om det känns lättare 🎙️</span>
            <span>{text.length} / 8000</span>
          </div>
        </div>

        <button
          onClick={send}
          disabled={sending || !text.trim()}
          className="neo-btn w-full py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60 transition"
          style={{ background: sending ? '#eee' : meta.accent }}
          data-testid="diary-send-btn"
        >
          {sending ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> {meta.label.toLowerCase()} tänker...</>
          ) : (
            <><Send className="w-4 h-4" /> Skicka — få svar {meta.label.toLowerCase()}</>
          )}
        </button>
      </div>

      {/* Entries list */}
      <div>
        {loading ? (
          <div className="neo-card p-6 text-center text-sm text-black/60 bg-white">Laddar...</div>
        ) : entries.length === 0 ? (
          <div className="neo-card p-8 text-center bg-white">
            <Flower2 className="w-10 h-10 mx-auto text-[#FFC8DD] mb-2" />
            <h3 className="font-bold">Ditt första inlägg väntar</h3>
            <p className="text-sm text-black/70 mt-1 max-w-md mx-auto">
              Det finns ingen rätt eller fel här. Ingen ser det. Skriv som om du skrev till någon som bara bryr sig. 💛
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-sm font-bold opacity-70 px-1">
              Dina tidigare inlägg ({entries.length})
            </h3>
            {entries.map((e) => (
              <EntryCard
                key={e.id}
                entry={e}
                onDelete={del}
                onAddTask={addTaskFromStep}
                onAddPostit={addPostitFromStep}
              />
            ))}
          </div>
        )}
      </div>

      {/* Gentle crisis footer */}
      <div className="neo-card p-3 bg-[#FFF7E0] text-[11px] text-center text-black/70 leading-relaxed">
        💛 Om du mår riktigt dåligt — du är inte ensam.{' '}
        <a href="tel:1177" className="underline font-bold">1177</a> (dygnet runt),{' '}
        <a href="tel:90101" className="underline font-bold">Mind 90101</a>,{' '}
        <a href="https://www.bris.se" target="_blank" rel="noreferrer" className="underline font-bold">BRIS</a> finns där.
      </div>
    </div>
  );
}
